import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';

interface VwOrb {
  x: number; // 0..1, fraction of canvas width
  y: number; // 0..1, fraction of canvas height
  r: number; // radius in px
  hue: number;
  vx: number;
  vy: number;
  phase: number;
}

// A soft, animated, purple-toned canvas background for public pages
// (login, self-check-in) — drifting blurred orbs with a gentle parallax
// tilt on mouse/touch movement. Pure Canvas2D, no new dependency: this
// substitutes for a three.js scene (which would need a new library, its
// own type-defs pinned to this app's old TypeScript 3.7 toolchain, and a
// non-trivial webpack budget increase) with the same "modern, interactive,
// depth-driven motion" effect at effectively zero integration risk.
@Component({
  selector: 'app-login-scene',
  template: '<canvas #canvas class="vw-login-scene"></canvas>',
  styleUrls: ['./login-scene.component.scss'],
})
export class LoginSceneComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvasRef: ElementRef<HTMLCanvasElement>;

  private ctx: CanvasRenderingContext2D;
  private orbs: VwOrb[] = [];
  private rafId: number;
  private width = 0;
  private height = 0;
  private mouseX = 0.5;
  private mouseY = 0.5;
  private parallaxX = 0.5;
  private parallaxY = 0.5;
  private destroyed = false;

  private readonly onResize = () => this.resize();
  private readonly onPointerMove = (e: MouseEvent | TouchEvent) => {
    const point = 'touches' in e ? e.touches[0] : e;
    if (!point) { return; }
    this.mouseX = point.clientX / window.innerWidth;
    this.mouseY = point.clientY / window.innerHeight;
  };

  ngAfterViewInit(): void {
    // Respect reduced-motion preference — render one static frame instead
    // of a continuous animation loop.
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.ctx = this.canvasRef.nativeElement.getContext('2d');
    this.resize();
    this.buildOrbs();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('mousemove', this.onPointerMove, { passive: true });
    window.addEventListener('touchmove', this.onPointerMove, { passive: true });

    if (reduceMotion) {
      this.draw(0);
    } else {
      this.animate(0);
    }
  }

  private buildOrbs(): void {
    // Purple/violet family matching the app's brand tokens (--vw-indigo/-violet).
    const hues = [258, 268, 280, 248];
    this.orbs = Array.from({ length: 6 }).map((_, i) => ({
      x: Math.random(),
      y: Math.random(),
      r: 140 + Math.random() * 180,
      hue: hues[i % hues.length],
      vx: (Math.random() - 0.5) * 0.00025,
      vy: (Math.random() - 0.5) * 0.00025,
      phase: Math.random() * Math.PI * 2,
    }));
  }

  private resize(): void {
    const canvas = this.canvasRef.nativeElement;
    this.width = canvas.width = canvas.offsetWidth;
    this.height = canvas.height = canvas.offsetHeight;
  }

  private animate = (t: number): void => {
    if (this.destroyed) {
      return;
    }
    // Ease the parallax toward the actual pointer position rather than
    // snapping — reads as a smooth 3D-ish tilt instead of a jump.
    this.parallaxX += (this.mouseX - this.parallaxX) * 0.03;
    this.parallaxY += (this.mouseY - this.parallaxY) * 0.03;
    this.draw(t);
    this.rafId = requestAnimationFrame(this.animate);
  };

  private draw(t: number): void {
    const ctx = this.ctx;
    if (!ctx || this.width === 0) {
      return;
    }
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    ctx.clearRect(0, 0, this.width, this.height);

    const parallaxStrength = 40;
    const offsetX = (this.parallaxX - 0.5) * parallaxStrength;
    const offsetY = (this.parallaxY - 0.5) * parallaxStrength;

    this.orbs.forEach(orb => {
      orb.x += orb.vx;
      orb.y += orb.vy;
      // Bounce softly off the edges instead of wrapping, for a calmer drift.
      if (orb.x < -0.2 || orb.x > 1.2) { orb.vx *= -1; }
      if (orb.y < -0.2 || orb.y > 1.2) { orb.vy *= -1; }

      const drift = Math.sin(t / 4000 + orb.phase) * 12;
      const cx = orb.x * this.width + offsetX + drift;
      const cy = orb.y * this.height + offsetY + drift * 0.6;

      const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, orb.r);
      const alpha = isDark ? 0.30 : 0.16;
      gradient.addColorStop(0, `hsla(${orb.hue}, 85%, ${isDark ? 68 : 58}%, ${alpha})`);
      gradient.addColorStop(1, `hsla(${orb.hue}, 85%, ${isDark ? 68 : 58}%, 0)`);

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(cx, cy, orb.r, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('mousemove', this.onPointerMove);
    window.removeEventListener('touchmove', this.onPointerMove);
  }
}
